
export interface Article {
  id: number;
  title: string;
  excerpt: string;
  content: string;
  coverImage: string;
  publishDate: string;
  author: string;
  category: string;
}

export const mockArticles: Article[] = [
  {
    id: 1,
    title: "租屋合約必看條款：保障你的權益",
    excerpt: "了解租屋合約中的關鍵條款，避免常見的合約陷阱，有效保障自身權益。",
    content: `
## 租屋合約必看條款：保障你的權益

簽訂租屋合約是租屋過程中最重要的一步，一份好的合約能保障雙方權益，避免未來產生不必要的糾紛。以下是幾項租屋合約中必須注意的關鍵條款：

### 1. 租金及支付方式

確保合約清楚列明月租金額、何時支付、支付方式等資訊。若房東要求現金支付，記得索取收據或使用能留下支付紀錄的方式，如轉帳。

### 2. 租期及續租條件

明確標示租期起訖日期，以及續租的條件和流程。部分合約會有自動續租條款，請特別注意這些細節。

### 3. 押金金額及退還條件

合約應清楚說明押金金額、用途，以及退還的條件和時間。依法押金不得超過二個月租金，並應於租期結束、返還房屋後一個月內返還。

### 4. 房屋維修責任

確認誰負責房屋的日常維修和重大修繕，以及相關費用的負擔方式。一般而言，結構性問題應由房東負責，日常使用問題則由租客負責。

### 5. 公共費用負擔

明確水電、網路、管理費、瓦斯等各項費用的負擔方式，避免日後產生爭議。

### 6. 提前終止合約條款

了解雙方若需提前終止合約的條件、通知期限及可能的賠償金額。這對於可能需要臨時搬遷的租客尤為重要。

### 7. 轉租權利

若未來可能需要轉租，請確認合約是否允許轉租，以及相關條件限制。

### 8. 房屋現況及設備清單

在簽約時，雙方應共同確認房屋現況及設備清單，並附於合約中，以避免退租時對房屋狀況產生爭議。

### 9. 違約責任

了解若任一方違反合約條款時的責任和賠償方式，確保條款公平合理。

### 10. 其他特殊約定

任何雙方的特殊約定，如是否允許養寵物、房東進入房屋的條件等，都應在合約中明確列出。

簽約前，建議仔細閱讀每一條款，若有不清楚或不合理的地方，應與房東協商修改。若涉及較大金額或長期租約，可考慮請法律專業人士協助審閱。記住，一份清楚、公平的租約是租屋生活順利的重要基礎。
    `,
    coverImage: "https://source.unsplash.com/random/800x600?contract",
    publishDate: "2023-04-15",
    author: "李律師",
    category: "法律知識",
  },
  {
    id: 2,
    title: "如何有效與房東溝通：解決問題的技巧",
    excerpt: "良好的溝通是解決租屋問題的關鍵，本文提供實用的溝通技巧和範例。",
    content: `
## 如何有效與房東溝通：解決問題的技巧

與房東保持良好的溝通關係，能讓您的租屋生活更加順暢。以下是一些有效的溝通技巧，幫助您在面對各種租屋問題時，能夠更好地與房東協商：

### 1. 選擇適當的溝通時機和方式

* 非緊急事項：選擇工作日的上午或下午，避開太早或太晚的時間
* 緊急狀況：立即通知，但態度需保持冷靜
* 根據房東偏好的聯繫方式（電話、簡訊、email或通訊軟體）進行溝通

### 2. 清晰表達問題和需求

* 具體描述問題：不要只說「水管壞了」，而是「廚房水槽下的水管漏水，地板已有積水」
* 提供相關證據：拍照或錄影記錄問題狀況
* 說明影響：「漏水導致無法使用廚房，且擔心會影響樓下住戶」

### 3. 提出合理的解決方案

* 先自行思考可能的解決方案
* 提出多個選項供房東參考
* 說明您願意配合的部分（例如在家等待維修人員）

### 4. 保持禮貌和尊重

* 即使感到沮喪，也要保持專業和禮貌
* 避免情緒性語言或指責
* 感謝房東的回應和協助

### 5. 重要溝通應有書面記錄

* 重要討論後以簡訊或email總結共識
* 保留所有溝通紀錄和收據
* 若涉及合約修改，確保有書面文件

### 6. 尋求協商和妥協

* 理解房東可能有的限制和考量
* 願意在非原則性問題上妥協
* 尋找互利的解決方案

### 7. 溝通實例

**範例1：報修請求**

> 「王先生您好，我是3樓租客小李。今早發現廚房水槽下方的水管有漏水情況，目前已用盆子接著，但水漏得較快。我有拍照紀錄（附照片）。因為擔心會影響樓下住戶，希望能儘快處理。我明天全天都在家，如果您方便安排水電師傅來修理，我可配合開門。謝謝您的協助！」

**範例2：噪音投訴回應**

> 「林太太您好，關於您提到的噪音問題，非常抱歉造成困擾。我會特別注意週間晚上10點後和週末早上9點前不製造噪音。同時，我也會在地板上增加地毯，減少行走聲音。如有其他建議，歡迎隨時告知。」

### 8. 無法達成共識時的處理

* 持續保持禮貌，避免衝突升級
* 提議找第三方（如管委會）協助調解
* 了解並引用相關法規或租約條款
* 必要時尋求法律諮詢

記住，良好的溝通是建立在相互理解和尊重的基礎上。大多數房東都希望維持良好的租賃關係，若能以合適的方式提出問題和需求，通常能得到積極的回應。
    `,
    coverImage: "https://source.unsplash.com/random/800x600?communication",
    publishDate: "2023-05-22",
    author: "張心理師",
    category: "溝通技巧",
  },
  {
    id: 3,
    title: "租金補貼與稅務減免：你不可不知的資訊",
    excerpt: "政府提供多種租金補貼方案，了解申請條件與流程，並掌握租屋相關稅務減免。",
    content: `
## 租金補貼與稅務減免：你不可不知的資訊

### 一、租金補貼方案

#### 1. 內政部「社會住宅包租代管第3期計畫」

**補貼對象：** 符合特定條件的一般戶、弱勢戶
**補貼金額：** 每月最高可補助8,000元
**申請條件：**
- 家庭年所得低於50萬元
- 家庭成員名下均無自有住宅
- 承租住宅未享有其他住宅補貼
- 租金補貼每戶每月最高8,000元，實際補貼金額依地區及租金額度不同而異

**申請流程：**
1. 向各直轄市、縣（市）政府提出申請
2. 準備身分證明、租賃契約、財產資料、所得資料等文件
3. 經審核通過後按月撥款

#### 2. 勞工住宅貸款利息補貼

**補貼對象：** 勞工保險被保險人
**補貼內容：** 住宅貸款利息部分補貼
**申請條件：**
- 累計投保年資滿5年以上
- 無自有住宅或為自有住宅尚在貸款中
- 家庭年收入在一定標準以下

#### 3. 大專校院弱勢學生助學計畫─校外住宿租金補貼

**補貼對象：** 符合低收入戶、中低收入戶或大專校院弱勢學生助學計畫資格，且未在校內住宿的學生
**補貼金額：** 依地區不同，每月1,200-1,800元不等
**申請方式：** 向就讀學校提出申請

### 二、租屋稅務減免

#### 1. 綜合所得稅「租金支出」列舉扣除

**適用對象：** 所有納稅人
**減免內容：** 每年租金支出可扣除額上限為12萬元
**申請條件：**
- 申報綜合所得稅時選擇列舉扣除額
- 需有正式租賃契約
- 需有合法的付款證明（如：銀行轉帳記錄、收據等）

**注意事項：**
- 需與出租人有正式租賃契約關係
- 房東需報稅，否則租客可能無法享有扣除
- 只適用於本人、配偶或受扶養親屬居住的房屋

#### 2. 房屋稅優惠（出租人適用）

**適用對象：** 房屋出租人
**優惠內容：** 自用住宅稅率（1.2%）比出租稅率（1.5%-3.6%）低
**申請條件：** 房屋為自住使用

#### 3. 包租代管稅務優惠

**適用對象：** 參與內政部「社會住宅包租代管計畫」的房東
**優惠內容：**
- 房東每月租金收入免稅額度由1萬提高至2萬元
- 綜合所得稅租金所得可享43%費用率
- 地價稅、房屋稅適用自用住宅稅率
- 契稅及印花稅減免

### 三、申請租金補貼的實用建議

1. **提前準備文件**：各項補貼皆需準備相關證明文件，建議提前準備
2. **注意申請時間**：大多數補貼計畫有固定申請期間，錯過需等下一期
3. **租約正規化**：確保有正式合法的租賃契約，有助於申請各項補貼
4. **保留付款證明**：妥善保存每月租金支付的相關證明
5. **主動了解資格**：經常查詢政府網站，了解是否符合新推出的補貼方案

租金補貼和稅務減免政策可能會隨政策調整而變動，建議定期查詢內政部、財政部或各地方政府網站，以獲取最新資訊。若有疑問，可撥打內政部營建署專線或各地方政府住宅服務的諮詢電話獲取協助。
    `,
    coverImage: "https://source.unsplash.com/random/800x600?finance",
    publishDate: "2023-06-10",
    author: "王會計師",
    category: "財務資訊",
  },
  {
    id: 4,
    title: "租屋安全檢查清單：入住前必做的10項檢查",
    excerpt: "從電路安全到逃生路線，這些入住前的檢查可能救你一命。",
    content: `
## 租屋安全檢查清單：入住前必做的10項檢查

在簽約租屋前，安全檢查是不可或缺的步驟。以下是10項關鍵的安全檢查事項，可以幫助您確保未來的住所安全無虞。

### 1. 電路系統檢查

* **老舊插座**：檢查插座是否有燒焦痕跡、鬆動或損壞
* **電線狀況**：觀察明線是否老舊、破損或不當配置
* **總電源箱**：確認總開關和斷路器運作正常，標示清楚
* **插座數量**：評估是否足夠，避免過度使用延長線造成負荷

### 2. 防火安全設施

* **煙霧偵測器**：確認是否安裝且功能正常
* **滅火器**：檢查是否配置滅火器及有效期限
* **防火門**：公寓大樓應確認防火門功能正常，不被阻擋
* **逃生路線**：了解至少兩條逃生路線，確保暢通無阻

### 3. 瓦斯安全檢查

* **瓦斯管線**：檢查管線是否老舊、生鏽或有異常氣味
* **熱水器安裝**：確認是否安裝在通風良好的位置，避免室內安裝
* **瓦斯爐具**：測試點火是否正常，火焰應為藍色
* **一氧化碳偵測器**：建議安裝，特別是使用瓦斯熱水器的住所

### 4. 門窗安全

* **大門鎖具**：檢查鎖具是否穩固，建議更換新鎖
* **窗戶鎖**：確認所有窗戶都有功能正常的鎖
* **緊急出口**：確保有通暢的緊急出口，尤其是頂樓加蓋的套房
* **防盜措施**：評估是否需要加裝額外的安全設施，如門鍊或門擋

### 5. 結構安全

* **牆面裂縫**：檢查牆面是否有明顯裂縫，特別是交接處
* **地板平整度**：確認地板是否平整，無明顯傾斜或下陷
* **天花板狀況**：觀察是否有水漬、裂痕或剝落
* **樓梯和扶手**：確保穩固無損壞

### 6. 水電漏水問題

* **水管漏水**：檢查水槽下方、馬桶周圍、淋浴間是否有漏水跡象
* **水壓測試**：測試所有水龍頭的水壓是否正常
* **排水系統**：確認排水是否順暢，無異味或堵塞
* **牆面與天花板水漬**：檢查是否有水漬，可能表示樓上漏水

### 7. 通風與霉菌問題

* **自然通風**：評估自然光和空氣流通情況
* **霉斑檢查**：檢查浴室、廚房和牆角是否有霉斑
* **異味**：注意是否有霉味或其他異常氣味
* **排風設備**：確認抽風機、排氣扇功能正常

### 8. 建築物公共安全

* **大樓管理**：了解是否有管理組織及24小時管理
* **公共區域安全**：檢查樓梯、走廊照明是否充足
* **社區安全措施**：確認門禁系統、監視器運作狀況
* **緊急求救系統**：了解社區是否有緊急聯絡方式

### 9. 周邊環境安全

* **社區治安**：諮詢當地居民或查詢犯罪統計資料
* **照明設施**：評估夜間回家路線的照明狀況
* **鄰近建築**：注意周圍是否有廢棄或危險建築
* **自然災害風險**：了解區域是否位於淹水區或土石流潛勢區

### 10. 其他安全考量

* **床位安全**：若有高架床，確認結構穩固
* **家具固定**：大型家具應能固定於牆面，避免地震時傾倒
* **室內階梯或高差**：確認有適當照明和扶手
* **寵物安全**：若有養寵物，確保窗戶、陽台等處無法讓寵物意外墜落

### 安全檢查表格

| 檢查項目 | 合格 | 不合格 | 備註 |
|---------|-----|-------|------|
| 電路系統 |     |       |      |
| 防火設施 |     |       |      |
| 瓦斯安全 |     |       |      |
| 門窗安全 |     |       |      |
| 結構安全 |     |       |      |
| 水電漏水 |     |       |      |
| 通風霉菌 |     |       |      |
| 公共安全 |     |       |      |
| 周邊環境 |     |       |      |
| 其他考量 |     |       |      |

進行這些安全檢查時，建議拍照記錄問題點，並與房東討論改善方案。若發現嚴重安全疑慮，應慎重考慮是否適合入住，因為住所安全關係到您的生命財產安全，不容忽視。
    `,
    coverImage: "https://source.unsplash.com/random/800x600?safety",
    publishDate: "2023-07-05",
    author: "陳工程師",
    category: "安全知識",
  },
  {
    id: 5,
    title: "小坪數空間規劃：讓你的租屋生活更舒適",
    excerpt: "在有限空間裡創造最大的舒適感，實用的收納技巧和空間規劃秘訣。",
    content: `
## 小坪數空間規劃：讓你的租屋生活更舒適

在大都市租屋，多數人面臨的共同挑戰就是如何在小坪數空間中創造舒適的生活環境。以下分享一些實用的空間規劃技巧，幫助您在有限的租屋空間中打造理想的居住環境。

### 一、空間規劃的基本原則

#### 1. 功能分區明確化

即使是小空間，也需要明確區分不同功能區域，如睡眠、工作、用餐和休閒區。可以使用以下方法：

* **家具擺放**：利用沙發、書架等作為分隔
* **地毯界定**：不同功能區使用不同地毯
* **照明區分**：各區域使用不同風格或亮度的燈光
* **色彩區隔**：運用色彩心理學，為不同區域賦予不同色調

#### 2. 垂直空間最大化

小坪數最寶貴的資源是垂直空間，有效利用可大幅增加收納和使用面積：

* **牆面收納**：安裝層板、掛鉤、壁掛式家具
* **高櫃選擇**：選擇較高但深度較淺的櫃體
* **床下空間**：選擇有收納功能的床架或抽屜
* **天花板吊掛**：輕物品可考慮吊掛方式存放

#### 3. 多功能家具優先

每一件家具都應該盡可能具備多種功能：

* **沙發床**：日間是沙發，夜間成為客人床鋪
* **收納型茶几**：表面可放置物品，內部有收納空間
* **可伸縮餐桌**：平時收合，有客人時展開
* **樓梯收納櫃**：若有夾層，樓梯每一階都可作為抽屜

### 二、小坪數空間的視覺擴增技巧

#### 1. 色彩運用

* **主色調選擇**：牆面和大型家具選用淺色系，如白色、米色、淺灰
* **重點色規劃**：選擇1-2種強調色作為點綴，避免過多色彩
* **漸層感創造**：從深到淺的漸層可增加空間深度感

#### 2. 光線優化

* **自然光最大化**：窗簾選擇透光材質，避免阻擋自然光
* **鏡面反射**：策略性放置鏡子反射光線和景觀
* **光源多樣化**：結合頂燈、壁燈、檯燈創造層次

#### 3. 視覺延伸

* **開放式設計**：盡可能減少隔間
* **統一地板材質**：全屋使用相同地板增加連續性
* **延伸線條**：利用條紋或線性元素延伸視線

### 三、各功能區域的規劃建議

#### 1. 客廳/起居空間

* **家具尺寸**：選擇比例適中的沙發，避免過大家具
* **電視牆設計**：結合收納和裝飾功能
* **動線規劃**：確保行走路徑暢通，至少保留60-90公分通道

#### 2. 臥室空間

* **床位選擇**：考慮平台床或榻榻米方案
* **衣物收納**：善用衣櫃內部分隔和真空收納袋
* **床頭設計**：床頭櫃選擇懸浮式或極簡設計

#### 3. 廚房設計

* **收納垂直化**：安裝壁掛式餐具架、磁性刀架
* **折疊工作台**：需要時展開，不用時收起
* **電器整合**：選擇多功能電器，減少單一功能電器數量

#### 4. 浴室空間

* **牆面收納**：安裝壁掛式置物架、吸盤式收納
* **門後空間**：充分利用門後安裝掛鉤或收納袋
* **選擇合適設備**：考慮小型但功能完整的衛浴設備

### 四、收納系統的建立

#### 1. 物品分類與取捨

* **定期整理**：每季檢視物品，移除不必要的物品
* **使用頻率分類**：依使用頻率決定存放位置
* **一進一出原則**：購入新物品時，丟棄或捐贈舊物品

#### 2. 收納用品選擇

* **統一風格**：選擇風格一致的收納盒，視覺上更整潔
* **透明收納盒**：方便辨識內容物
* **可堆疊設計**：最大化垂直空間利用

#### 3. 隱形空間挖掘

* **門後空間**：安裝門後掛架
* **櫃門內側**：增加小型掛勾或收納袋
* **傢俱間隙**：客製化抽拉式收納

### 五、實用案例分析

#### 案例1：12坪單身套房改造

**原始問題**：
- 空間狹小，需要包含客廳、臥室和工作區
- 收納空間不足
- 視覺上顯得壅擠

**解決方案**：
1. 採用開放式設計，移除非必要隔間
2. 將床設計為抬高的平台床，下方為收納和工作區
3. 電視牆結合收納和展示功能
4. 選用折疊式餐桌，不用時可完全收起

**成效**：
- 創造出三個功能明確的區域
- 增加了30%的收納空間
- 視覺上更加開闊通透

#### 案例2：8坪老屋廚房改造

**原始問題**：
- 動線不良，使用不便
- 收納嚴重不足
- 設備老舊且佔空間

**解決方案**：
1. 重新規劃L型流理台，優化動線
2. 安裝天花板至地面的細長型收納櫃
3. 選用迷你版家電和2口小型爐具
4. 增加壁掛式折疊工作台

**成效**：
- 工作效率提升50%
- 收納容量翻倍
- 保持了足夠的活動空間

小坪數空間規劃的秘訣在於每一寸空間都要發揮最大價值，同時不能犧牲生活的舒適度。透過多功能設計、垂直空間利用和視覺擴增技巧，即使是小套房也能創造出舒適宜人的居住環境。
    `,
    coverImage: "https://source.unsplash.com/random/800x600?smallspace",
    publishDate: "2023-08-18",
    author: "林設計師",
    category: "空間設計",
  },
];
